'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Flame, Zap, Shield, Sword, Mountain } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function SeptBatonsPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Bâtons</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Sept</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/batons"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Bâtons'a Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 rounded-xl p-8 mb-8 border border-red-600/30">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl bg-gradient-to-b from-red-100 to-orange-50 border border-red-300 flex flex-col items-center justify-center relative">
                {/* Card Visual Design */}
                <div className="text-center">
                  <div className="flex flex-col items-center mb-4">
                    <div className="flex gap-1 mb-1">
                      <div className="text-sm">🏴</div>
                      <div className="text-sm">🏴</div>
                      <div className="text-sm">🏴</div>
                    </div>
                    <div className="flex gap-1 mb-1">
                      <div className="text-sm">🏴</div>
                      <div className="text-lg">⚔️</div>
                      <div className="text-sm">🏴</div>
                    </div>
                    <div className="flex gap-1">
                      <div className="text-sm">🏴</div>
                    </div>
                  </div>
                  <div className="w-16 h-1 bg-red-600 mx-auto mb-4"></div>
                  <div className="text-2xl font-bold text-red-800">VII</div>
                  <div className="text-sm text-red-700 mt-2">de Bâtons</div>
                </div>
                {/* Decorative elements */}
                <div className="absolute top-4 left-4 text-red-600">
                  <Shield className="h-4 w-4" />
                </div>
                <div className="absolute top-4 right-4 text-red-600">
                  <Sword className="h-4 w-4" />
                </div>
                <div className="absolute bottom-4 left-4 text-red-600">
                  <Mountain className="h-4 w-4" />
                </div>
                <div className="absolute bottom-4 right-4 text-red-600">
                  <Shield className="h-4 w-4" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-orange-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">VII</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Sept de Bâtons</h1>
                  <p className="text-xl text-red-400">Savunma / Azim</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde savunma ve azmin kartı. Yüksek bir konumdan mücadele eden
                figür, kazanılmış pozisyonunu koruma kararlılığını simgeler. Zorluklara rağmen
                duruşunu korumanın ve sebat etmenin gücü.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-red-600/20 text-red-300 rounded-full text-sm">Savunma</span>
                <span className="px-3 py-1 bg-red-600/20 text-red-300 rounded-full text-sm">Azim</span>
                <span className="px-3 py-1 bg-red-600/20 text-red-300 rounded-full text-sm">Dayanıklılık</span>
                <span className="px-3 py-1 bg-red-600/20 text-red-300 rounded-full text-sm">Mücadele</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Flame className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kararlı Savunma</h3>
                  <p className="text-slate-400 text-sm">Pozisyonunu koruma, ilkelerin savunusu.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Azim ve Sebat</h3>
                  <p className="text-slate-400 text-sm">Zorluklara rağmen devam etme, pes etmeme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Avantajlı Pozisyon</h3>
                  <p className="text-slate-400 text-sm">Yüksek konumdan mücadele, üstünlük.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Cesaret</h3>
                  <p className="text-slate-400 text-sm">Korkuyla yüzleşme, meydan okuma.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Zap className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ezici Baskı</h3>
                  <p className="text-slate-400 text-sm">Aşırı zorluklarla karşılaşma, bunalım.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Pes Etme</h3>
                  <p className="text-slate-400 text-sm">Mücadeleyi bırakma, teslim olma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Paranoya</h3>
                  <p className="text-slate-400 text-sm">Aşırı savunmacılık, gereksiz kaygılar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İnatçılık</h3>
                  <p className="text-slate-400 text-sm">Gereksiz direnç, esneklik eksikliği.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yüksek Zemin</h3>
              <p className="text-slate-400 text-sm">Stratejik avantaj, güvenli pozisyon.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Sword className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Savunma Duruşu</h3>
              <p className="text-slate-400 text-sm">Kararlılık, dayanıklılık ve mücadele.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Mountain className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yedi Değnek</h3>
              <p className="text-slate-400 text-sm">Manevi güç, iç direniş ve sebat.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Rekabetçi ortamda savunma</li>
                <li>• Pozisyonu koruma</li>
                <li>• Zorlu projeler</li>
                <li>• Liderlik sınavları</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Flame className="h-5 w-5 text-red-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İç güç geliştirme</li>
                <li>• Dayanıklılık artırma</li>
                <li>• İlkeleri savunma</li>
                <li>• Cesaret bulma</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/batons/six"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Six de Bâtons
          </Link>

          <Link
            href="/about/tarot/systems/marseille/minor-arcana/batons/huit"
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Huit de Bâtons
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}